import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import {
  faCirclePlus,
  faImage,
  faMessageDots,
  faPaperPlaneAlt,
  faWarning,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu } from '@mui/material';
import debounce from 'lodash/debounce';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';

import {
  BulkMessageSegment,
  ClientMembersDocument,
  CoachMemberListDocument,
  SelfDocument,
  SendBulkMessageDocument,
  UserAutocompleteDetailsFragment,
} from '../../../graphql/types';

import useTracking from '../../../hooks/useTracking';
import { BLACK2, WHITE } from '../../../theme';
import {
  EventCategories,
  EventLabels,
  EventNames,
  EventProfileCardNames,
  EventSubCategories,
} from '../../../utils/trackingEvents';
import Button, { ButtonSize, ButtonVariant } from '../../reusable/Button';
import { IconButton } from '../../reusable/Button/IconButton';
import { Divider } from '../../reusable/Divider';
import Flyout, { FlyoutItem } from '../../reusable/Flyout';
import {
  MemberBulkAutocomplete,
  MemberScope,
} from '../../reusable/MemberBulkAutocomplete';
import { SnackbarVariant } from '../../reusable/Snackbar';
import { useSnackbarContext } from '../../reusable/Snackbar/SnackbarContextProvider';
import Text, { TextKind } from '../../reusable/Text';
import TextAreaInput from '../../reusable/TextAreaInput';
import { ChatSize, useChatContext } from '../Context';
import { Header } from '../Header';
import { FilePreview } from '../Image';
import { useSendMessage } from '../useSendMessage';
import {
  StyledForm,
  StyledFormContent,
  StyledFormFooter,
  TemplateItem,
} from './styled';

interface CreateBulkChatValues {
  memberIds: Array<string>;
  bulkMessage: string;
}

interface CreateBulkChatProps {
  closeable?: boolean;
}

interface BulkTemplate {
  label: string;
  value: string;
}

const bulkTemplates: BulkTemplate[] = [
  { label: 'Member First Name', value: '{{first}}' },
];

// Number of members fetched per page, for the initial load, search, and each
// infinite-scroll page.
const MEMBERS_PAGE_SIZE = 20;

// Page size when resolving the full member list of a "Select all" scope.
const SELECT_ALL_RESOLVE_PAGE_SIZE = 100;

// Individual removal from a "Select all" resolves the whole scope at
// SELECT_ALL_RESOLVE_PAGE_SIZE members per (sequential) request and renders
// the result as a plain selection, so it is blocked for scopes where that
// would hang the UI.
const SELECT_ALL_REMOVE_MEMBER_LIMIT = 300;

const SEGMENT_BY_SCOPE: Record<MemberScope, BulkMessageSegment> = {
  assigned: BulkMessageSegment.AssignedMembers,
  all: BulkMessageSegment.AllMembers,
};

export const CreateBulkChat: React.FC<CreateBulkChatProps> = ({
  closeable,
}) => {
  const methods = useForm<CreateBulkChatValues>({
    defaultValues: {
      memberIds: [],
      bulkMessage: '',
    },
  });
  const { handleSubmit, watch, formState } = methods;
  const { chatSize } = useChatContext();
  const { setSnackbar } = useSnackbarContext();
  const { track } = useTracking();
  const { validateFileInput, uploadFile } = useSendMessage();

  const { data: selfData } = useQuery(SelfDocument);
  const [sendBulkChat] = useMutation(SendBulkMessageDocument);

  const selfId = selfData?.self?.id;
  const [
    fetchCoachMemberList,
    { data: userListData, loading: userListLoading },
  ] = useLazyQuery(CoachMemberListDocument);
  const [memberScope, setMemberScope] = useState<MemberScope>('assigned');
  // Pages a single client's members in isolation: coachMemberList drives every
  // client's `users` connection with one shared cursor, which cannot page one
  // client without corrupting the others.
  const [fetchClientMembers] = useLazyQuery(ClientMembersDocument, {
    // The merged list is already cached, so a cache read would never request
    // the next page; force the network round-trip for each page.
    fetchPolicy: 'network-only',
  });
  // Resolves the member list of a "Select all" scope when a member is removed
  // from it. no-cache keeps these sweeps out of the visible list's cache.
  const [fetchSelectAllMembers] = useLazyQuery(CoachMemberListDocument, {
    fetchPolicy: 'no-cache',
  });
  const [fetchSelectAllMemberPage] = useLazyQuery(ClientMembersDocument, {
    fetchPolicy: 'no-cache',
  });
  // Shows a spinner while a removal is resolving the scope's member list, and
  // (via the ref) drops further clicks until the first one settles.
  const [resolvingSelectAll, setResolvingSelectAll] = useState(false);
  const resolvingSelectAllRef = useRef(false);
  const [loadingMoreMembers, setLoadingMoreMembers] = useState(false);
  // Synchronous guards for load-more: state flips too late to gate the burst
  // of scroll events a gesture fires in one tick, which would request the same
  // page repeatedly.
  const loadMoreInFlightRef = useRef(false);
  const lastRequestedPageRef = useRef<string | null>(null);
  // "Assigned members" restricts the search to the coach's own members;
  // "All members" searches everyone in the coach's client org(s).
  const buildMembersFilter = useCallback(
    (scope: MemberScope, name?: string) => ({
      hasSignedIn: true,
      ...(name ? { name } : {}),
      ...(scope === 'assigned' && selfId ? { coachId: [selfId] } : {}),
    }),
    [selfId],
  );
  const membersFilter = useMemo(
    () => buildMembersFilter(memberScope),
    [buildMembersFilter, memberScope],
  );
  // Latest filter for the (stable) debounced search, so a late-firing search
  // uses the current scope rather than one captured at schedule time.
  const membersFilterRef = useRef(membersFilter);
  membersFilterRef.current = membersFilter;

  // The exact filter the visible list was last fetched with. Load-more must
  // page with it verbatim so the page lands in the same Client.users({filter})
  // cache bucket the list reads from.
  const activeListFilterRef = useRef(membersFilter);

  const [selectedMembers, setSelectedMembers] = useState<
    UserAutocompleteDetailsFragment[]
  >([]);
  const [mergedMemberList, setMergedMemberList] = useState<
    UserAutocompleteDetailsFragment[]
  >([]);
  const [showAllPills, setShowAllPills] = useState(false);
  // Scope fully selected via "Select all", or null for individual picks.
  const [selectAllScope, setSelectAllScope] = useState<MemberScope | null>(
    null,
  );
  // Unfiltered member count for the "All …" tag; same variables as the scope's
  // list fetch so it reads from the cache.
  const { data: selectAllCountData } = useQuery(CoachMemberListDocument, {
    skip: !selectAllScope,
    variables: {
      membersFilter: selectAllScope
        ? buildMembersFilter(selectAllScope)
        : undefined,
      input: { first: MEMBERS_PAGE_SIZE },
    },
  });
  const selectAllCount = useMemo(() => {
    const clients = selectAllCountData?.self?.coachData?.clients.edges;
    if (!selectAllScope || !clients) {
      return null;
    }
    return clients.reduce((sum, { node }) => sum + node.users.count, 0);
  }, [selectAllScope, selectAllCountData]);
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [showTemplateItems, setShowTemplateItems] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileUploadInputRef = useRef<HTMLInputElement>(null);

  const memberIdsWatcher = watch('memberIds');
  const messageWatcher = watch('bulkMessage');
  const actionMenuButtonRef = useRef(null);

  const reset = useCallback(() => {
    setShowAllPills(false);
    methods.setValue('memberIds', []);
    methods.setValue('bulkMessage', '');
    setSelectedMembers([]);
    setSelectAllScope(null);
    setFile(null);
    setAutocompleteKey((prevKey) => prevKey + 1);
  }, [methods]);

  // Resolves every member of a "Select all" scope by paging through each
  // client. Returns null when any page fails (no partial recipient lists).
  const resolveSelectAllMembers = useCallback(
    async (
      scope: MemberScope,
    ): Promise<UserAutocompleteDetailsFragment[] | null> => {
      const filter = buildMembersFilter(scope);
      try {
        const listResult = await fetchSelectAllMembers({
          variables: {
            membersFilter: filter,
            input: { first: SELECT_ALL_RESOLVE_PAGE_SIZE },
          },
        });
        const clients = listResult?.data?.self?.coachData?.clients.edges;
        if (!clients) {
          return null;
        }
        const membersById = new Map<string, UserAutocompleteDetailsFragment>();
        for (const { node: client } of clients) {
          client.users.edges.forEach(({ node }) =>
            membersById.set(node.id, node),
          );
          let { pageInfo } = client.users;
          while (pageInfo.hasNextPage && pageInfo.endCursor) {
            const pageResult = await fetchSelectAllMemberPage({
              variables: {
                clientId: client.id,
                membersFilter: filter,
                input: {
                  first: SELECT_ALL_RESOLVE_PAGE_SIZE,
                  after: pageInfo.endCursor,
                },
              },
            });
            const users = pageResult?.data?.client?.users;
            if (!users) {
              return null;
            }
            users.edges.forEach(({ node }) => membersById.set(node.id, node));
            ({ pageInfo } = users);
          }
        }
        return [...membersById.values()];
      } catch {
        return null;
      }
    },
    [buildMembersFilter, fetchSelectAllMembers, fetchSelectAllMemberPage],
  );

  // Removing a member from "Select all" resolves the scope's full member list
  // and continues as an individual selection of everyone else.
  const removeMemberFromSelectAll = useCallback(
    async (scope: MemberScope, removedMemberId: string) => {
      if (resolvingSelectAllRef.current) {
        return;
      }
      // An unknown count is blocked too: resolving blind could hang just the
      // same.
      if (selectAllCount == null) {
        setSnackbar({
          icon: faWarning,
          message:
            'Still loading the audience size. Try removing the member again in a moment.',
          variant: SnackbarVariant.Danger,
          testId: 'bulk-chat-remove-loading',
        });
        return;
      }
      if (selectAllCount > SELECT_ALL_REMOVE_MEMBER_LIMIT) {
        setSnackbar({
          icon: faWarning,
          message:
            'This audience is too large to remove individual members from. Select members individually instead.',
          variant: SnackbarVariant.Danger,
          testId: 'bulk-chat-remove-limit',
        });
        return;
      }
      resolvingSelectAllRef.current = true;
      setResolvingSelectAll(true);
      const members = await resolveSelectAllMembers(scope);
      resolvingSelectAllRef.current = false;
      setResolvingSelectAll(false);

      if (!members) {
        setSnackbar({
          icon: faWarning,
          message: 'An error occurred while removing the member.',
          variant: SnackbarVariant.Danger,
          testId: 'bulk-chat-error',
        });
        return;
      }

      const remainingMembers = members.filter(
        ({ id }) => id !== removedMemberId,
      );
      setSelectAllScope(null);
      setSelectedMembers(remainingMembers);
      methods.setValue(
        'memberIds',
        remainingMembers.map(({ id }) => id),
      );
    },
    [resolveSelectAllMembers, setSnackbar, methods, selectAllCount],
  );

  const handleFormSubmit = useCallback(
    async (values: CreateBulkChatValues) => {
      if (!selfId) {
        return;
      }

      const { memberIds, bulkMessage } = values;
      let messageContent = bulkMessage;

      if (file) {
        try {
          setUploadingFile(true);
          const { url } = await uploadFile(file);

          if (url) {
            messageContent = url;
          }
        } catch {
          setSnackbar({
            icon: faWarning,
            message: 'An error occurred while sending the bulk attachment.',
            variant: SnackbarVariant.Danger,
          });
        } finally {
          setUploadingFile(false);
        }
      }

      const bulkChat = await sendBulkChat({
        variables: {
          input: {
            // For a segment, the backend resolves the recipient list.
            ...(selectAllScope
              ? { segment: SEGMENT_BY_SCOPE[selectAllScope] }
              : {
                  destinations: memberIds.map((id) => {
                    return { userId: id };
                  }),
                }),
            text: messageContent,
          },
        },
        refetchQueries: ['chats'],
        onError: () =>
          setSnackbar({
            icon: faWarning,
            message: 'An error occurred while sending the bulk message.',
            variant: SnackbarVariant.Danger,
            testId: 'bulk-chat-error',
          }),
      });

      if (!bulkChat.error) {
        setSnackbar({
          icon: faPaperPlaneAlt,
          message: 'Message sent successfully! This can take a few minutes.',
          variant: SnackbarVariant.Success,
          testId: 'bulk-chat-success',
        });
        // Keep the selected members so the user can send follow-up messages
        // to the same group; only clear the message content.
        methods.reset({ memberIds, bulkMessage: '' });
        setFile(null);
      }
    },
    [
      selfId,
      sendBulkChat,
      setSnackbar,
      methods,
      file,
      uploadFile,
      selectAllScope,
    ],
  );

  // Memoize the debounced search so there is a single instance across renders
  // (a per-render debounce never actually debounces and leaves orphaned timers).
  const handleAutocompleteInput = useMemo(
    () =>
      debounce((value: string) => {
        // A new search replaces the list, so allow its first page to paginate
        // again.
        lastRequestedPageRef.current = null;
        const filter = {
          ...(value ? { name: value } : {}),
          ...membersFilterRef.current,
        };
        activeListFilterRef.current = filter;
        fetchCoachMemberList({
          variables: {
            membersFilter: filter,
            input: {
              first: MEMBERS_PAGE_SIZE,
            },
          },
        });
      }, 500),
    [fetchCoachMemberList],
  );

  // Cancel any pending debounced search on unmount.
  useEffect(
    () => () => handleAutocompleteInput.cancel(),
    [handleAutocompleteInput],
  );

  // Loads the next page one client at a time, using that client's own cursor.
  // clientMembers writes to the same normalized Client.users({filter}) field
  // the list reads, so the page merges in without touching other clients.
  const handleLoadMoreMembers = useCallback(() => {
    if (loadMoreInFlightRef.current) {
      return;
    }

    const nextClient = userListData?.self?.coachData?.clients.edges.find(
      ({ node }) => node.users.pageInfo.hasNextPage,
    )?.node;

    if (!nextClient) {
      return;
    }

    // Don't re-request a page already requested for this cursor; the next
    // render advances the cursor, so genuine subsequent pages still load.
    const pageKey = `${nextClient.id}:${nextClient.users.pageInfo.endCursor}`;
    if (lastRequestedPageRef.current === pageKey) {
      return;
    }

    loadMoreInFlightRef.current = true;
    lastRequestedPageRef.current = pageKey;
    setLoadingMoreMembers(true);
    fetchClientMembers({
      variables: {
        clientId: nextClient.id,
        // Reuse the exact filter the visible list was loaded with so the page
        // lands in the same cache bucket (Client.users is keyed by filter).
        membersFilter: activeListFilterRef.current,
        input: {
          first: MEMBERS_PAGE_SIZE,
          after: nextClient.users.pageInfo.endCursor,
        },
      },
    }).finally(() => {
      loadMoreInFlightRef.current = false;
      setLoadingMoreMembers(false);
    });
  }, [userListData?.self?.coachData?.clients.edges, fetchClientMembers]);

  const handleSelection = (
    selectedUsers: UserAutocompleteDetailsFragment[],
  ) => {
    // While "Select all" is active the controlled value is empty, so a click
    // arrives as a single-member array: remove that member. A click in a
    // different scope than the fully selected one starts a new individual
    // selection instead.
    if (selectAllScope && selectAllScope === memberScope) {
      const removedMember = selectedUsers[selectedUsers.length - 1];
      if (removedMember) {
        removeMemberFromSelectAll(selectAllScope, removedMember.id);
      }
      return;
    }
    if (selectAllScope) {
      setSelectAllScope(null);
    }
    // The autocomplete is value-controlled, so `selectedUsers` is the full set
    // of selected members. Setting (rather than remounting) keeps the selector
    // open for the next pick.
    methods.setValue(
      'memberIds',
      selectedUsers.map((user) => user.id),
    );
  };

  const handleDeletePill = (deletedUserId: string) => {
    methods.setValue(
      'memberIds',
      memberIdsWatcher.filter((selectedUser) => selectedUser !== deletedUserId),
    );
    setSelectedMembers((current) =>
      current.filter((member) => member.id !== deletedUserId),
    );
  };

  const handleRemoveAll = () => {
    methods.setValue('memberIds', []);
    setSelectedMembers([]);
    setShowAllPills(false);
  };

  const handleSelectAllChange = (scope: MemberScope | null) => {
    setSelectAllScope(scope);
    if (scope) {
      // "Select all" supersedes any individual selection.
      methods.setValue('memberIds', []);
      setSelectedMembers([]);
      setShowAllPills(false);
    }
  };

  const handleMemberScopeChange = (scope: MemberScope) => {
    // Drop any pending debounced search so it can't fire after (and override)
    // the fresh fetch for the new scope.
    handleAutocompleteInput.cancel();
    setMemberScope(scope);
    // The new scope replaces the list, so allow its first page to paginate again.
    lastRequestedPageRef.current = null;
    // Refetch the full (unfiltered) list for the newly selected scope. The
    // autocomplete clears its search input on scope change so the two agree.
    const filter = buildMembersFilter(scope);
    activeListFilterRef.current = filter;
    fetchCoachMemberList({
      variables: {
        input: {
          first: MEMBERS_PAGE_SIZE,
        },
        membersFilter: filter,
      },
    });
  };

  const handleTemplateMenuOpen = () => {
    setShowTemplateItems(true);
    track(EventNames.MenuItemSelect, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.BulkTemplate,
      event_label: 'Browse bulk message templates',
    });
  };

  const handleTemplateClick = (template: string) => {
    setShowTemplateItems(false);
    const prevMessage = methods.getValues('bulkMessage') || '';
    const newMessage =
      prevMessage.trim() !== ''
        ? `${prevMessage.trim()} ${template}`
        : template;
    methods.setValue('bulkMessage', newMessage);
  };

  const trackActionMenuOpen = () => {
    track(EventNames.PlusIconClick, {
      event_category: EventCategories.Chat,
    });
  };

  const handleFlyoutOpen = () => {
    setShowTemplateItems(false);
    trackActionMenuOpen();
  };

  const handleSelectFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    track(EventNames.FileSelection, {
      chat_id: 'bulk-chat',
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Image,
      event_label: event.target.files?.[0].type.split('/')[1],
    });
    setFile(validateFileInput(event));
  };

  const handleRemoveFile = () => {
    setFile(null);
    track(EventNames.RemoveAttachment, {
      event_category: EventCategories.Chat,
      event_subcategory: EventSubCategories.Image,
      chat_id: 'bulk-chat',
    });
  };

  const handleUploadImageClick = () => {
    track(EventNames.MenuItemSelect, {
      event_category: EventCategories.Chat,
      event_label: 'Upload an image',
      event_subcategory: EventSubCategories.Image,
      chat_id: 'bulk-chat',
    });
    if (fileUploadInputRef.current?.value) {
      fileUploadInputRef.current.value = '';
    }
    fileUploadInputRef.current?.click();
  };

  const getAutoCompleteMaxHeight = useCallback(() => {
    if (chatSize === ChatSize.Maximized) {
      return '450px';
    }
    return '300px';
  }, [chatSize]);

  const getPreviewMaxHeight = useCallback(() => {
    if (chatSize === ChatSize.Maximized) {
      return showAllPills ? '340px' : '500px';
    }
    return showAllPills ? '60px' : '230px';
  }, [chatSize, showAllPills]);

  // Reset the form whenever the chat is closed.
  useEffect(() => {
    if (chatSize === ChatSize.Closed) {
      methods.reset();
    }
  }, [chatSize, methods.reset]);

  // Fetch the initial member list once per open, waiting for selfId so the
  // default "assigned" scope is coachId-scoped from the first request.
  const initialFetchDoneRef = useRef(false);
  useEffect(() => {
    if (chatSize === ChatSize.Closed) {
      initialFetchDoneRef.current = false;
      return;
    }
    if (initialFetchDoneRef.current) {
      return;
    }
    if (memberScope === 'assigned' && !selfId) {
      // Wait for selfId so coachId can be applied to the assigned scope.
      return;
    }
    initialFetchDoneRef.current = true;
    const filter = buildMembersFilter(memberScope);
    activeListFilterRef.current = filter;
    fetchCoachMemberList({
      variables: {
        input: {
          first: MEMBERS_PAGE_SIZE,
        },
        membersFilter: filter,
      },
    });
  }, [chatSize, memberScope, selfId, fetchCoachMemberList, buildMembersFilter]);

  useEffect(() => {
    const clientNodes =
      userListData?.self?.coachData?.clients.edges.map(({ node }) => node) ??
      [];

    // Reveal clients sequentially — stop at the first client that still has
    // more pages — so a freshly fetched page always lands at the end of the
    // list instead of above the next client's members.
    const visibleMembers: UserAutocompleteDetailsFragment[] = [];
    for (const client of clientNodes) {
      visibleMembers.push(...client.users.edges.map(({ node }) => node));
      if (client.users.pageInfo.hasNextPage) {
        break;
      }
    }

    setMergedMemberList([
      ...visibleMembers.filter(
        ({ id }) =>
          !selectedMembers.some(
            ({ id: selectedMemberId }) => id === selectedMemberId,
          ),
      ),
      ...selectedMembers,
    ]);
  }, [userListData?.self?.coachData?.clients.edges, selectedMembers]);

  useEffect(() => {
    const newSelectedMembers = memberIdsWatcher
      .filter((id) => !selectedMembers.some((member) => member.id === id))
      .map((id) => mergedMemberList.find((member) => member.id === id))
      .filter((member) => member !== undefined);

    if (newSelectedMembers.length > 0) {
      setSelectedMembers((currentList) => [
        ...currentList,
        ...newSelectedMembers,
      ]);
    }
  }, [memberIdsWatcher, mergedMemberList, selectedMembers]);

  useEffect(() => {
    setMergedMemberList((currentList) => [
      ...currentList.filter(
        ({ id }) =>
          !selectedMembers.some(({ id: existingId }) => existingId === id),
      ),
      ...selectedMembers,
    ]);
  }, [selectedMembers]);

  return (
    <>
      <Header
        content={{ title: 'New message', hideEmptyElements: true }}
        closeable={closeable}
        resetSelectedMembers={reset}
      />
      <FormProvider {...methods}>
        <StyledForm
          onSubmit={handleSubmit(handleFormSubmit)}
          data-testid="createBulkChat-form"
        >
          <StyledFormContent>
            <Controller
              name="memberIds"
              rules={{
                validate: (memberIds: string[]) =>
                  memberIds.length > 0 ||
                  selectAllScope !== null ||
                  'At least one member is required',
              }}
              render={({ field }) => (
                <MemberBulkAutocomplete
                  key={autocompleteKey}
                  name={field.name}
                  selectedMemberIds={field.value || []}
                  members={mergedMemberList || []}
                  onInputChange={handleAutocompleteInput}
                  onSelection={handleSelection}
                  onDeletePill={handleDeletePill}
                  onLoadMore={handleLoadMoreMembers}
                  loadingMore={loadingMoreMembers}
                  placeholder="Search for members"
                  loading={userListLoading || resolvingSelectAll}
                  trackingData={{
                    event_category: EventCategories.Chat,
                    card_name: EventProfileCardNames.CreateBulkChat,
                  }}
                  showAllPills={showAllPills}
                  setShowAllPills={setShowAllPills}
                  memberScope={memberScope}
                  onMemberScopeChange={handleMemberScopeChange}
                  onRemoveAll={handleRemoveAll}
                  selectAllScope={selectAllScope}
                  selectAllCount={selectAllCount}
                  onSelectAllChange={handleSelectAllChange}
                  selectorMaxHeight={getAutoCompleteMaxHeight()}
                />
              )}
            />
            {file ? (
              <FilePreview
                file={file}
                loading={uploadingFile}
                onRemoveFile={handleRemoveFile}
                previewStyle={{ maxHeight: getPreviewMaxHeight() }}
              />
            ) : (
              <TextAreaInput
                formRegister={methods.register('bulkMessage', {
                  required: 'Message is required',
                })}
                placeholder="Write a message"
                backgroundColor={WHITE}
                height="100%"
                trackingData={{
                  event_category: EventCategories.Chat,
                  card_name: EventProfileCardNames.CreateBulkChat,
                  event_label: EventLabels.CreateBulkChat,
                }}
                onFocus={() => setShowAllPills(false)}
              />
            )}
          </StyledFormContent>

          <Divider $marginBottom={0} $marginTop={0} />

          <StyledFormFooter>
            <Button
              type="submit"
              variant={ButtonVariant.Dark}
              icon={<FontAwesomeIcon icon={faPaperPlaneAlt} />}
              trackingData={{
                event_category: EventCategories.Chat,
                card_name: EventProfileCardNames.CreateBulkChat,
                event_label: EventLabels.Create,
              }}
              disabled={formState.isSubmitting || uploadingFile}
              loading={formState.isSubmitting || uploadingFile}
              ariaLabel="Send message"
            >
              Send
            </Button>

            <input
              data-testid="bulk-chat-image-upload"
              ref={fileUploadInputRef}
              type="file"
              accept={[
                'image/jpeg',
                'image/jpg',
                'image/png',
                'application/pdf',
              ].join(',')}
              multiple={false}
              style={{
                display: 'none',
              }}
              onChange={handleSelectFile}
            />

            <Flyout
              flyoutId="ActionMenu"
              menuProps={{
                anchorOrigin: { vertical: 'top', horizontal: 'left' },
                transformOrigin: { vertical: 'bottom', horizontal: 'left' },
              }}
              onOpen={handleFlyoutOpen}
              trigger={
                <IconButton
                  ariaLabel="Open more actions"
                  size={ButtonSize.Small}
                  icon={
                    <FontAwesomeIcon
                      ref={actionMenuButtonRef}
                      fontSize={20}
                      icon={faCirclePlus}
                    />
                  }
                />
              }
              tooltipContent="Open more actions"
            >
              <FlyoutItem
                onClick={handleTemplateMenuOpen}
                textKind={TextKind.LabelL}
                startIcon={
                  <FontAwesomeIcon icon={faMessageDots} color={BLACK2} />
                }
              >
                Browse template tags
              </FlyoutItem>
              <FlyoutItem
                onClick={() => handleUploadImageClick()}
                textKind={TextKind.LabelL}
                disabled={Boolean(messageWatcher)}
                startIcon={<FontAwesomeIcon icon={faImage} color={BLACK2} />}
              >
                Upload a file
              </FlyoutItem>
            </Flyout>
            <Menu
              style={{ marginTop: -16 }}
              disableScrollLock
              anchorEl={actionMenuButtonRef.current}
              open={showTemplateItems}
              onBlur={() => setShowTemplateItems(false)}
              MenuListProps={{
                disablePadding: true, // Disable default MUI Menu padding of 8px top and bottom
              }}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 8 }}
            >
              {bulkTemplates.map((template) => (
                <TemplateItem
                  data-testid={`template-${template.value}`}
                  key={template.value}
                  onClick={() => handleTemplateClick(template.value)}
                >
                  <Text kind={TextKind.BodySM} textTransform="none">
                    {template.label}
                  </Text>
                </TemplateItem>
              ))}
            </Menu>
          </StyledFormFooter>
        </StyledForm>
      </FormProvider>
    </>
  );
};
